
import Vue, { VueConstructor } from "vue";
import KCrudTable, {
  CrudTableHeader,
} from "@/modules/crudTable/components/KCrudTable.vue";
import { ExportType, PaginatedRequest } from "@/application/api/getPaginated";
import { mapGetters } from "vuex";
import RequiredClientDialog from "@/modules/client/components/RequiredClientDialog.vue";
import {
  clientConceptSalesOrderIndex,
  conceptSalesOrderIndex,
  ConceptSalesOrderIndexItem,
} from "@/modules/salesOrder/api/conceptSalesOrderIndex";
import { conceptSalesOrderDestroy } from "@/modules/salesOrder/api/conceptSalesOrderDestroy";
import { bulkSend } from "@/modules/salesOrder/api/bulkSend";
import eventBus from "@/application/eventBus";
import ImportDialog from "@/modules/salesOrder/components/ImportDialog.vue";
import { exportSalesOrders } from "@/modules/salesOrder/api/exportImport";
import { sendBlobToBrowserDownload } from "@/application/util/downloadFile";
import SalesOrderFilter from "@/modules/salesOrder/components/SalesOrderFilter.vue";
import BulkDeleteButton from "@/modules/crudTable/components/BulkDeleteButton.vue";
import OrderStatusChip from "@/modules/salesOrder/components/OrderStatusChip.vue";
import ExportButton from "@/components/ExportButton.vue";
import NavigationBtnToInformation from "@/components/NavigationBtnToInformation.vue";

interface ComponentData {
  headers: CrudTableHeader[];
  selected: Partial<ConceptSalesOrderIndexItem> &
    { clientId: number; id: number }[];
  isImportDialogOpen: boolean;
  filterComponent: VueConstructor;
  defaultFilters: Record<string, unknown>;
  exports?: ExportType[];
}

export default Vue.extend({
  name: "ConceptSalesOrderTable",
  components: {
    NavigationBtnToInformation,
    ExportButton,
    OrderStatusChip,
    BulkDeleteButton,
    ImportDialog,
    RequiredClientDialog,
    KCrudTable,
  },
  data: (): ComponentData => ({
    headers: [
      {
        value: "orderDate",
        type: "date",
        width: "185px",
        typeParameters: {
          dateType: "DD MMM YYYY HH:mm",
          tooltipProperty: "errors",
        },
      },
      { value: "orderNumber", typeParameters: { tooltipProperty: "errors" } },
      { value: "companyName", typeParameters: { tooltipProperty: "errors" } },
      { value: "fullName", typeParameters: { tooltipProperty: "errors" } },
      { value: "city", typeParameters: { tooltipProperty: "errors" } },
      { value: "country", typeParameters: { tooltipProperty: "errors" } },
      {
        value: "originalSource",
        typeParameters: { tooltipProperty: "errors" },
      },
      { value: "statusId" },
    ],
    selected: [],
    exports: [],
    isImportDialogOpen: false,
    filterComponent: SalesOrderFilter,
    defaultFilters: {},
  }),
  computed: {
    ...mapGetters("authorisation", ["client", "isClient", "clients"]),
  },
  watch: {
    client: {
      handler() {
        this.resetTable();
      },
      deep: true,
    },
  },
  created() {
    if (!this.isClient) {
      this.headers.unshift({ value: "clientName" });
    }
    this.setDefaultFilters();
  },
  methods: {
    setDefaultFilters() {
      if (this.isClient) return;
      this.defaultFilters = {
        dateFrom: undefined,
        dateTo: undefined,
      };
    },
    selectItems(ids: number[]): void {
      if (!this.client?.id) throw "Selecting id's when clientId is not set"; //@TODO for now this function is only used when client is set

      this.selected = ids.map((id) => ({ id, clientId: this.client?.id })); //@TODO
    },
    async conceptSalesOrderIndex(data: PaginatedRequest) {
      const response = await (!this.client && !this.isClient
        ? conceptSalesOrderIndex(data)
        : clientConceptSalesOrderIndex(data, this.client?.id));
      this.exports = response.data.export ?? [];
      return response;
    },
    salesOrderDestroy(item: ConceptSalesOrderIndexItem) {
      return conceptSalesOrderDestroy({
        clientId: item.clientId,
        salesOrderId: item.id,
      });
    },
    handleEdit(item: ConceptSalesOrderIndexItem) {
      this.$router.push({
        name: "salesOrder.edit",
        params: {
          salesOrderId: item.id.toString(),
          clientId: item.clientId.toString(),
        },
      });
    },
    handleBulkEdit() {
      const salesOrderIds: string[] = this.selected.map((item) =>
        item.id.toString()
      );
      const clientIds: string[] = this.selected.map((item) =>
        item.clientId.toString()
      );

      this.$router.push({
        name: "salesOrder.edit",
        params: { salesOrderId: salesOrderIds[0], clientId: clientIds[0] },
        query: { salesOrderIds, clientIds },
      });
    },
    refreshTable(): void {
      (this.$refs.table as any).getData();
    },
    resetTable(): void {
      (this.$refs.table as any).handleReset();
    },
    async handleBulkSend() {
      try {
        const response = await bulkSend({
          clientId: this.client?.id, //@TODO
          ids: this.selected.map((item) => item.id),
        });
        const { succeeded, failed } = response.data.data;
        this.alertSucceededAndFailedSendings(succeeded.length, failed.length);
        this.deselectItems(succeeded);
        this.refreshTable();
      } catch (e) {
        eventBus.$emit("snackbar", {
          text: this.$t("global.error"),
          color: "error",
        });
        throw e;
      }
    },
    alertSucceededAndFailedSendings(succeeded: number, failed: number) {
      eventBus.$emit("snackbar", {
        text: this.$t("salesOrder.messages.bulkSend", {
          succeeded: succeeded,
          failed: failed,
        }),
        color: succeeded > 0 ? "success" : "error",
      });
    },
    deselectItems(ids: number[]) {
      this.selected = this.selected.filter((item) => !ids.includes(item.id));
    },
    async downloadExport() {
      const response = await exportSalesOrders(this.client.id);
      sendBlobToBrowserDownload("salesOrders", "xlsx", response.data);
    },
  },
});
